import { useState, useEffect } from 'react'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import db1 from '../../../assets/Database/db1.png'
import db2 from '../../../assets/Database/db2.png'
import './Database.css'

const Database = () => {
  const [activeStep, setActiveStep] = useState(1)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [lightboxImage])

  const steps = [
    {
      id: 1,
      title: 'Create a Database',
      description: 'Deploy a managed database instance with your preferred engine and configuration.',
      image: db1,
      details: 'Click "New Database" or "Create Database" to open the creation modal. Select your database engine (PostgreSQL, MySQL, MariaDB, MongoDB, or Redis), choose an instance tier (Starter, Standard, Professional, or Enterprise), select the version, configure storage using the slider, and choose replication options. Review the total monthly cost and click "Create Database" to provision your instance.'
    },
    {
      id: 2,
      title: 'Monitor Database Status and View Details',
      description: 'Track your database instance from creation to running, and access comprehensive details and connection information.',
      image: db2,
      details: (
        <>
          After clicking "Create Database", your instance will begin provisioning. Monitor the status transition from <strong>Pending</strong> → <strong>Creating</strong> → <strong>Running</strong> in real-time. Once the status shows <strong>Running</strong>, click on your database instance to access the detail page.
          <br /><br />
          The <strong>Overview</strong> tab displays resource specifications (engine, CPU, memory, storage, replication), instance metadata, and live connection statistics. The <strong>Connection</strong> tab provides connection strings, CLI commands, and individual credentials (host, port, username, password, database name). You can copy credentials with one click and rotate them for security.
        </>
      )
    },
    {
      id: 3,
      title: 'Manage Backups',
      description: 'Create backups, schedule automatic backups, and restore from backups.',
      details: 'Navigate to the Backups tab to manage your database backups. Enable daily backup schedule and configure the backup time and retention period (3-90 days). Create manual backups at any time by clicking "New Backup". View backup status, download backup files, or restore from a backup. Backups protect your data and allow point-in-time recovery.'
    },
    {
      id: 4,
      title: 'Connect and Use',
      description: 'Connect your applications to the database using connection strings or CLI commands.',
      details: (
        <>
          Use the connection details from the Connection tab to connect your applications. Connection strings are provided in standard format for each database engine:
          <pre><code>{`# PostgreSQL
postgresql://user:password@host:5432/dbname

# MySQL/MariaDB
mysql://user:password@host:3306/dbname

# MongoDB
mongodb://user:password@host:27017/dbname

# Redis
redis://host:6379`}</code></pre>
          You can also use CLI commands provided in the Connection tab. Copy the connection string or CLI command and use it in your application's environment variables. Keep credentials secure and never commit them to version control.
        </>
      )
    }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const allStepIds = steps.map(s => ({ id: `database-step-${s.id}`, stepId: s.id }))
      const scrollPosition = window.scrollY + 200

      for (let i = allStepIds.length - 1; i >= 0; i--) {
        const element = document.getElementById(allStepIds[i].id)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveStep(allStepIds[i].stepId)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [steps])

  return (
    <div className="database-page">
      <div className="database-content-wrapper">
        <div className="database-main-content">
      <h1>Database</h1>
      <p className="intro">
            Create and manage databases on Aedify.AI. Support for PostgreSQL, MySQL, MariaDB, MongoDB, 
            and Redis with automatic backups, monitoring, and scaling.
      </p>

          <section className="database-section">
            <h2>Getting Started with Database</h2>
            <p className="section-intro">
              Follow these steps to create your database, manage backups, and connect your applications.
        </p>

            {steps.map((step) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id ? 'active' : ''}`}
                id={`database-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                {step.image && (
                  <div className="step-image">
                    <img 
                      src={step.image} 
                      alt={`Step ${step.id}: ${step.title}`}
                      onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                      className="clickable-image"
                    />
                  </div>
                )}
                <div className="step-details">
                  {typeof step.details === 'string' ? <p>{step.details}</p> : step.details}
                </div>
              </div>
            ))}
      </section>

      {/* <section>
            <h2>Database Engines</h2>
        <p>
              Aedify.AI supports multiple database engines, each optimized for different use cases:
        </p>
            <ul>
              <li><strong>PostgreSQL</strong> - Advanced open-source relational database with JSON support</li>
              <li><strong>MySQL</strong> - Popular relational database for web applications</li>
              <li><strong>MariaDB</strong> - MySQL-compatible database with enhanced features</li>
              <li><strong>MongoDB</strong> - NoSQL document database for flexible data structures</li>
              <li><strong>Redis</strong> - In-memory data store for caching and real-time applications</li>
        </ul>
      </section> */}

      <section>
            <h2>Instance Tiers</h2>
        <p>
              Choose the right tier for your workload:
            </p>
            <ul>
              <li><strong>Starter</strong> - 0.5 vCPU, 1 GB RAM, 25 connections - Development & testing</li>
              <li><strong>Standard</strong> - 1 vCPU, 2 GB RAM, 100 connections - Small production apps</li>
              <li><strong>Professional</strong> - 2 vCPU, 4 GB RAM, 200 connections - Growing applications</li>
              <li><strong>Enterprise</strong> - 4 vCPU, 8 GB RAM, 400 connections - High-traffic production</li>
            </ul>
        <p>
              Storage is configurable from 10 GB to 500 GB, with additional cost per GB. You can scale 
              your database vertically by upgrading tiers or horizontally by adding read replicas.
        </p>
      </section>

      <section>
            <h2>High Availability</h2>
        <p>
              Configure replication for high availability and read scaling. Choose between:
            </p>
            <ul>
              <li><strong>1 node</strong> - Single primary node with daily backups</li>
              <li><strong>2 nodes</strong> - Primary + 1 replica for high availability</li>
              <li><strong>3 nodes</strong> - Primary + 2 replicas for enhanced redundancy</li>
            </ul>
            <p>
              Replicated databases provide automatic failover, synchronous replication, and read scaling 
              capabilities. The number of replicas depends on your selected tier.
        </p>
      </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Step Navigation */}
        <aside className="database-sidebar">
          <div className="sidebar-section">
            <h3>Getting Started</h3>
            <nav className="step-nav">
              {steps.map((step) => (
                <a
                  key={step.id}
                  href={`#database-step-${step.id}`}
                  className={`step-nav-item ${activeStep === step.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveStep(step.id)
                    document.getElementById(`database-step-${step.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="step-nav-number">{step.id}</span>
                  <span className="step-nav-title">{step.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Tips</h3>
            <ul className="tips-list">
              <li>Start with Starter tier for development and testing</li>
              <li>Use Standard or higher for production workloads</li>
              <li>Enable daily backups and configure appropriate retention</li>
              <li>Monitor connection usage and upgrade tier if needed</li>
              <li>Use connection pooling to manage database connections efficiently</li>
              <li>Keep credentials secure and rotate them regularly</li>
              <li>Test backups regularly to ensure they can be restored</li>
            </ul>
          </div>
        </aside>
      </div>

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  )
}

export default Database
